<template>

  <div class="flex justify-center items-center h-screen" v-if="state.pageLoadComplete === false">
    <span class="loader"></span>
  </div>

  <div
    v-else
    class="w-full max-w-[64rem] mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-12 pb-20 lg:pb-28"
  >
    <!-- 목록 상단 (페이지 이동 시 이 위치로 스크롤) -->
    <header
      ref="listTop"
      class="flex flex-wrap items-end justify-between gap-2 scroll-mt-[calc(var(--nav-height,80px)+1rem)]"
    >
<!--      2023 총회 여름성경학교.수련회 지도자 컨퍼런스-->
      <h1 class="page-title">컨퍼런스 신청내역</h1>
      <p
        v-if="state.myConference.registeredConferenceList.length > 0"
        class="text-[0.9375rem] text-gray-500"
      >
        총
        <b class="font-bold text-main-navy">{{
          state.myConference.registeredConferenceList.length
        }}</b
        >건
      </p>
    </header>

    <!-- <div class="w-full text-center text-xl mb-5"> ※ 취소 및 변경처리는 19일 오후부터 처리가능합니다 </div> -->

    <!-- 내역 없음 -->
    <div
      v-if="state.myConference.registeredConferenceList.length === 0"
      class="empty-state"
    >
      <span class="empty-icon" aria-hidden="true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.6"
          stroke="currentColor"
          class="h-7 w-7"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
          />
        </svg>
      </span>
      <p class="mt-4 text-base font-semibold text-gray-800">신청한 컨퍼런스가 없습니다</p>
      <router-link :to="{ name: 'eduList' }" class="empty-link">
        컨퍼런스 보러가기
      </router-link>
    </div>

    <ul v-else class="mt-6 space-y-4">
      <li
        v-for="(item, index) in confesOnCurrentPage"
        :key="item.idx ?? index"
        class="conf-card"
      >
        <div class="p-5 md:p-6">
          <!-- 결제·취소 상태 -->
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="status-chip" :class="statusClass(item.status)">
              {{ item.status }}
            </span>
            <span
              v-if="item.paymentCancelListLatest.status === '취소신청'"
              class="status-chip status-chip--waiting"
            >
              취소 확인중
            </span>
            <span
              v-else-if="item.paymentCancelListLatest.status === '처리완료'"
              class="status-chip status-chip--muted"
            >
              취소 처리완료
            </span>
          </div>

          <h2 class="conf-title">{{ item.conferenceDetail.conference.title }}</h2>
          <p class="conf-place">
            {{ placeText(item) }}
            <b v-if="item.conferenceDetail.dept" class="font-semibold text-gray-800">
              · {{ item.conferenceDetail.dept }}
            </b>
          </p>

          <dl class="conf-stats">
            <div>
              <dt>참가 인원</dt>
              <dd>{{ item.headcount }}명</dd>
            </div>
            <div>
              <dt>결제 금액</dt>
              <!-- total_price 가 로딩된 후 표시 -->
              <dd>{{ item.total_price ? formatPrice(item.total_price) : '-' }}원</dd>
            </div>
          </dl>
        </div>

        <!-- 버튼 -->
        <div class="conf-actions">
          <!-- 결제 계속 (접수 기간 내 결제시도 건) -->
          <button
            v-if="item.status === '결제시도' && viewCheck(item.conferenceDetail.redatetime) === false"
            type="button"
            class="action-button action-button--primary"
            @click="goPayment(item.idx, item.conferenceDetail.redatetime)"
          >
            결제 계속하기
          </button>

          <!-- 상세보기 -->
          <VDialog :classes="{ bg: 'bg-black/40' }" class="p-4" no-scroll>
            <template #default="{ close }">
              <div class="dialog-panel">
                <div class="dialog-head">
                  <h3 class="dialog-title">상세보기</h3>
                  <button type="button" class="dialog-close" aria-label="닫기" @click="close">
                    <font-awesome-icon icon="fa-solid fa-xmark" />
                  </button>
                </div>
                <div class="dialog-body">
                  <section>
                    <p class="dialog-label">컨퍼런스</p>
                    <p class="font-semibold text-gray-900">{{ item.conferenceDetail.conference.title }}</p>
                    <p class="mt-0.5 text-gray-600">
                      {{ placeText(item) }} {{ item.conferenceDetail.dept }}
                    </p>
                  </section>
                  <section>
                    <p class="dialog-label">참가자 (총 {{ item.headcount }}명)</p>
                    <ul class="member-list">
                      <li v-for="(ppl, idx) in activeMembers(item)" :key="idx">
                        <b class="font-semibold text-gray-900">{{ ppl.name }}</b>
                        <span class="text-gray-500">{{ ppl.church }}</span>
                      </li>
                    </ul>
                  </section>
                  <section>
                    <p class="dialog-label">결제 금액</p>
                    <p class="text-gray-900">
                      <b class="font-bold">{{ item.total_price ? formatPrice(item.total_price) : '-' }}원</b>
                      <span class="ml-1.5 text-gray-500">({{ item.status }})</span>
                    </p>
                  </section>
                </div>
              </div>
            </template>

            <template #toggle="{ bind, on }">
              <button v-bind="bind" v-on="on" class="action-button">
                <font-awesome-icon icon="fa-solid fa-magnifying-glass" class="action-icon" />
                상세보기
              </button>
            </template>
          </VDialog>

          <!-- 바코드 -->
          <VDialog v-if="item.barcode !== null" :classes="{ bg: 'bg-black/40' }" class="p-4" no-scroll>
            <template #default="{ close }">
              <div class="dialog-panel">
                <div class="dialog-head">
                  <h3 class="dialog-title">바코드 보기</h3>
                  <button type="button" class="dialog-close" aria-label="닫기" @click="close">
                    <font-awesome-icon icon="fa-solid fa-xmark" />
                  </button>
                </div>
                <div class="dialog-body">
                  <p class="text-center font-semibold text-gray-900 break-keep">
                    {{ item.conferenceDetail.conference.title }}
                  </p>
                  <div class="rounded-xl border border-gray-200 py-4">
                    <vue3-barcode :value="item.barcode" :height="50" class="flex justify-center"/>
                  </div>
                </div>
              </div>
            </template>

            <template #toggle="{ bind, on }">
              <button v-bind="bind" v-on="on" class="action-button">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-linecap="round"
                  class="action-icon"
                  aria-hidden="true"
                >
                  <path stroke-width="2" d="M4 6v12M10 6v12M17 6v12" />
                  <path stroke-width="1.2" d="M7 6v12M13 6v12M15 6v12M20 6v12" />
                </svg>
                바코드
              </button>
            </template>
          </VDialog>

          <!-- 영수증 출력 -->
<!--            <a href="https://www.inicis.com/payment-view" target="_blank" v-if="item.status == '등록완료'" class="justify-center py-3 bg-neutral-100 hover:bg-sky-100 flex items-center">-->
          <button
            v-if="item.status == '등록완료'"
            type="button"
            class="action-button"
            @click="openModal(item.tid)"
          >
            <font-awesome-icon icon="fa-solid fa-receipt" class="action-icon" />
            영수증
          </button>
<!--        <a :href="'https://iniweb.inicis.com/receipt/iniReceipt.jsp?noTid='+item.tid" target="_blank" v-if="item.status == '등록완료'" class="justify-center py-3 bg-neutral-100 hover:bg-sky-100 flex items-center">-->
<!--        </a>-->

          <!-- 취소/변경 신청 -->
          <VDialog
            v-if="item.status == '등록완료' && item.paymentCancelListLatest.status != '취소신청'"
            :classes="{ bg: 'bg-black/40' }"
            class="p-4"
            no-scroll
          >
            <template #default="{ close }">
              <div class="dialog-panel">
                <div class="dialog-head">
                  <h3 class="dialog-title">취소 신청</h3>
                  <button type="button" class="dialog-close" aria-label="닫기" @click="close">
                    <font-awesome-icon icon="fa-solid fa-xmark" />
                  </button>
                </div>
                <div class="dialog-body">
                  <section>
                    <p class="dialog-label">컨퍼런스</p>
                    <p class="font-semibold text-gray-900">{{ item.conferenceDetail.conference.title }}</p>
                    <p class="mt-0.5 text-gray-600">
                      {{ placeText(item) }} {{ item.conferenceDetail.dept }}
                    </p>
                    <p class="mt-1.5 text-gray-600">
                      결제금액 <b class="font-semibold text-gray-900">{{ item.total_price ? formatPrice(item.total_price) : '-' }}원</b>
                    </p>
                  </section>

                  <section>
                    <p class="dialog-label">요청 구분</p>
                    <div class="grid grid-cols-2 gap-2">
                      <label class="choice">
                        <input type="radio" name="type" class="choice-input" v-model="state.cancelApply.type" value="부분" @change="selectType('', item.headcount)" />
                        <span class="choice-box">부분취소</span>
                      </label>
                      <label class="choice">
                        <input type="radio" name="type" class="choice-input" v-model="state.cancelApply.type" value="전체" @change="selectType(item.total_price, item.headcount)" />
                        <span class="choice-box">전체취소</span>
                      </label>
                    </div>
                  </section>

                  <section v-show="state.cancelApply.type === '부분'">
                    <p class="dialog-label">취소인원선택</p>
                    <!-- 인원 선택 -->
                    <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <label v-for="(ppl, idx) in activeMembers(item)" :key="idx" class="choice">
                        <input type="checkbox" class="choice-input" v-model="state.cancelApply.member" :value="ppl.idx" @change="calcCanclePrice('', item.headcount)"/>
                        <span class="choice-box">{{ ppl.name }}</span>
                      </label>
                    </div>
                  </section>

                  <!-- 수수료, 환불금액 -->
                  <dl class="refund-box">
                    <div class="flex justify-between">
                      <dt class="text-gray-500">취소수수료</dt>
                      <dd>0원</dd>
                    </div>
                    <div class="flex justify-between">
                      <dt class="text-gray-500">환불금액</dt>
                      <dd class="text-lg font-bold text-main-navy">{{ formatPrice(state.cancelApply.price) }}원</dd>
                    </div>
                  </dl>

<!--                      <div>
                    <p class="text-sky-600">사유</p>
                    <textarea class="w-full border p-2"></textarea>
                  </div>-->
                  <button type="button" class="dialog-submit" @click="cancelApply( item.idx )">취소 신청하기</button>
                </div>
              </div>
            </template>

            <template #toggle="{ bind, on }">
              <button v-bind="bind" v-on="on" class="action-button" @click="setCancleInit">
                <font-awesome-icon icon="fa-solid fa-pen-to-square" class="action-icon" />
                취소·변경 신청
              </button>
            </template>
          </VDialog>
        </div>
      </li>
    </ul>

    <!-- 페이지 이동 -->
    <nav v-if="totalPages > 1" class="pager" aria-label="페이지 이동">
      <button
        type="button"
        class="pager-step"
        :disabled="state.currentPage === 1"
        aria-label="이전 페이지"
        @click="handlePageChange(state.currentPage - 1)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="h-4 w-4"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
        이전
      </button>

      <!-- 모바일: 현재 / 전체 -->
      <p class="pager-status sm:hidden" aria-live="polite">
        <b class="font-bold text-gray-900">{{ state.currentPage }}</b>
        <span class="mx-1 text-gray-300">/</span>
        {{ totalPages }}
      </p>

      <!-- 태블릿/데스크톱: 페이지 번호 -->
      <ul class="hidden sm:flex items-center gap-1">
        <li v-for="(page, i) in pageItems" :key="i">
          <span v-if="page === '…'" class="pager-gap" aria-hidden="true">…</span>
          <button
            v-else
            type="button"
            class="pager-num"
            :class="{ 'pager-num--active': page === state.currentPage }"
            :aria-current="page === state.currentPage ? 'page' : undefined"
            :aria-label="`${page} 페이지`"
            @click="handlePageChange(page)"
          >
            {{ page }}
          </button>
        </li>
      </ul>

      <button
        type="button"
        class="pager-step"
        :disabled="state.currentPage === totalPages"
        aria-label="다음 페이지"
        @click="handlePageChange(state.currentPage + 1)"
      >
        다음
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="h-4 w-4"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </button>
    </nav>

<!--    <div>
      <router-link to="/myCancelList" class="sec_blk_btn lg:px-5 px-2 py-2">취소·변경 내역</router-link>
    </div>-->
  </div>



  <ini-receipt  v-if="modalVisible" @close="modalVisible = false" :it_id="modalData" ></ini-receipt>



</template>
  
<script setup>
  import {reactive, computed, ref} from 'vue'
  import { VDialog } from 'vuetensils/src/components'
  import axios from 'axios'
  import { numberWithCommas } from "@/assets/js/util.js";

  import iniReceipt from './iniReceipt.vue'
  import ReviewWriteComponent from "@/components/ReviewWriteComponent.vue";

  import Vue3Barcode from 'vue3-barcode'
  
  const state = reactive({
    myConference: {
      registeredConferenceList: [
        {
          conferenceDetail: {
            conference: {
              title:"",
            }
          },
          registerMemberList: [{

          }],
	        paymentCancelList: [],
          paymentCancelListLatest: {
						idx: 0,
            status: "",
            type: "",
            price: 0,
            count: 0,
            cancel_member_idxs: "0",
            created_date: "",
            confirm_date: null,
            member_idx: 0
					}
        }
      ]
    },

    cancelApply: {
      price: 0,
      member: [],
      type: "부분"
    },

    currentPage: 1,
    pageSize: 8, //페이지에 8개씩

    pageLoadComplete: false
  })

  const selectType = ( total, totalPerson ) => {
    if( state.cancelApply.type === "부분" ) {
        state.cancelApply.member = [];
        state.cancelApply.price = 0;
    }
    calcCanclePrice(total, totalPerson);
  }

  const calcCanclePrice = ( total, totalPerson ) => {

    let price = 0
    if( state.cancelApply.type === "전체" ) {
      state.cancelApply.price = total;
    }
    else {
      state.myConference.registeredConferenceList.forEach(item => {
        item.registerMemberList.forEach(ppl => {
          if(state.cancelApply.member.includes(ppl.idx)) {
            price += item.conferenceDetail.price
          }
        })
      })

      if( totalPerson === state.cancelApply.member.length ){
        state.cancelApply.type = "전체";
      }
      console.log(price);
      console.log(state.cancelApply.member);
      state.cancelApply.price = price
    }
  }

   const cancelApply = async ( idx ) => {
    try {
      // const count = (state.cancelApply.type == "전체") ? 0 : state.cancelApply.member.length;
      const count = state.cancelApply.member.length;
      const cancel_member_idxs = state.cancelApply.member.join(',');

      const body = {
        "register_conf_idx": idx,
        "type": state.cancelApply.type,
        "price": state.cancelApply.price,
        "count": count,
        "cancel_member_idxs": cancel_member_idxs
      }

      if( body.type === "부분" && body.cancel_member_idxs === "" ) {
        alert("취소할 인원을 선택해주세요.");
        return;
      }

      const response = await axios.post('/api/payment/cancel', body);
      console.log(response);
      if (response.data.err_code === 0) {
        alert('신청이 완료되었습니다. \n\n처리 완료 후 안내드립니다.');
        document.location.reload();
      } else {
        alert(response.data.err_msg)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const goPayment = ( idx, endDate ) => {

    const date = new Date();
    const targetDate = new Date(endDate);
    if( date <= targetDate ) {
      if( window.location.pathname === '/profile/confList/app' ){
        document.location.href = '/app/register_pay/' + idx;
      }
      else {
        document.location.href = '/register_pay/' + idx;
      }
    }
    else {
      alert('접수 기간이 아닙니다.');
    }
  }

  const setCancleInit = () => {
    state.cancelApply = {
      price: 0,
      member: [],
      type: "부분"
    }
  }


  setTimeout(() => {

    // 오늘 날짜 yyyymmdd 형식으로
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so we add 1. padStart ensures it's always 2 digits
    const day = String(today.getDate()).padStart(2, '0'); // padStart ensures it's always 2 digits
    const formattedDate = `${year}-${month}-${day}`;

    console.log(formattedDate);



    const url = `/api/member/me?today=${formattedDate}`
    axios.get(url).then(response => {
      state.myConference = response.data.contents;
      state.myConference.registeredConferenceList.map(item => {
        item.paymentCancelListLatest = ( item.paymentCancelList.length > 0 ) ? item.paymentCancelList[item.paymentCancelList.length - 1] : {};
      })
      state.pageLoadComplete = true;
    }).catch(error => {
      if( window.location.pathname === '/profile/confList/app' ){
        alert('로그인이 필요합니다.');
        getData.postMessage('login');
      }
      console.error(error)
    })
  }, 1000)


  // 총 페이지수 계산
  const totalPages = computed(() =>
    Math.ceil(state.myConference.registeredConferenceList.length / state.pageSize)
  )

  //회원 목록 slice
  const confesOnCurrentPage = computed(() => {
    const start = (state.currentPage - 1) * state.pageSize
    const end = start + state.pageSize
    return state.myConference.registeredConferenceList.slice(start, end)
  })

  // 페이지 번호 목록 - 처음·끝·현재 주변만 보여주고 나머지는 … 로 줄인다
  const pageItems = computed(() => {
    const total = totalPages.value
    const current = state.currentPage
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1)
    }

    const pages = [...new Set([1, current - 1, current, current + 1, total])]
      .filter((page) => page >= 1 && page <= total)
      .sort((a, b) => a - b)

    return pages.reduce((items, page, i) => {
      const prev = pages[i - 1]
      if (prev && page - prev === 2) items.push(prev + 1)
      else if (prev && page - prev > 2) items.push('…')
      items.push(page)
      return items
    }, [])
  })

  //페이지 변경 - 목록 맨 위로 스크롤
  const listTop = ref(null)
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages.value || newPage === state.currentPage) return
    state.currentPage = newPage
    listTop.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // 결제 상태별 표시 색
  const STATUS_CLASSES = {
    등록완료: 'status-chip--done',
    결제대기: 'status-chip--waiting',
    결제실패: 'status-chip--failed',
  }
  const statusClass = (status) => STATUS_CLASSES[status] ?? 'status-chip--muted'

  // 지역 · 장소
  const placeText = (item) =>
    [item.conferenceDetail.local, item.conferenceDetail.place]
      .map((text) => (text ?? '').trim())
      .filter(Boolean)
      .join(' · ')

  // 취소되지 않은 참가자
  const activeMembers = (item) =>
    (item.registerMemberList ?? []).filter((ppl) => ppl.is_cancel == 'N')

  const formatPrice = (price) => numberWithCommas(Number(price) || 0)

  const viewCheck = ( endDate ) => {
    const date = new Date();
    const targetDate = new Date(endDate);
    return date > targetDate;
  }

  const modalVisible = ref(false);
  const modalData = ref(null);

  const openModal = (content) => {
    modalData.value = content;
    modalVisible.value = true;
  };

</script>

<style scoped>
.page-title {
  @apply text-2xl md:text-[2rem] font-bold tracking-tight text-gray-900;
}

/* 내역 없음 */
.empty-state {
  @apply mt-6 flex flex-col items-center rounded-2xl border border-dashed border-gray-300 px-6 py-16 text-center;
}

.empty-icon {
  @apply flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-gray-400;
}

.empty-link {
  @apply mt-5 inline-flex h-11 items-center rounded-xl bg-main-navy px-5 text-sm font-semibold text-white transition-colors hover:bg-main-navy/90;
}

/* 신청 카드 */
.conf-card {
  @apply overflow-hidden rounded-2xl border border-gray-200 bg-white;
}

.conf-title {
  @apply mt-3 text-lg md:text-xl font-bold leading-snug tracking-tight text-gray-900 break-keep;
}

.conf-place {
  @apply mt-1 text-[0.9375rem] text-gray-600 break-keep;
}

.conf-stats {
  @apply mt-4 flex flex-wrap gap-x-8 gap-y-2;
}

.conf-stats > div {
  @apply flex items-baseline gap-2;
}

.conf-stats dt {
  @apply text-sm text-gray-500;
}

.conf-stats dd {
  @apply text-[0.9375rem] font-semibold text-gray-900;
}

.conf-actions {
  @apply grid grid-cols-2 gap-2 border-t border-gray-100 bg-slate-50/70 px-5 py-4 sm:flex sm:flex-wrap md:px-6;
}

/* VDialog 토글은 span 으로 감싸져 있어 버튼이 칸을 채우도록 한다 */
.conf-actions > :deep(span) {
  @apply contents;
}

/* 전역 .vts-dialog__content(좌상단 고정) 대신 화면 가운데에 띄운다 */
.conf-actions :deep(.vts-dialog__content) {
  position: relative;
  top: auto;
  left: auto;
}

.action-button {
  @apply inline-flex h-11 sm:h-10 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-main-navy;
}

.action-button--primary {
  @apply col-span-2 border-main-navy bg-main-navy text-white hover:border-main-navy hover:bg-main-navy/90;
}

.action-icon {
  @apply h-4 w-4 text-gray-400;
}

/* 결제·취소 상태 */
.status-chip {
  @apply inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold leading-none;
}

.status-chip::before {
  content: "";
  @apply h-1.5 w-1.5 rounded-full bg-current;
}

.status-chip--done {
  @apply bg-emerald-50 text-emerald-700;
}

.status-chip--waiting {
  @apply bg-amber-50 text-amber-700;
}

.status-chip--failed {
  @apply bg-red-50 text-red-600;
}

.status-chip--muted {
  @apply bg-gray-100 text-gray-500;
}

/* 다이얼로그 */
.dialog-panel {
  @apply w-[min(32rem,calc(100vw-2rem))] max-h-[85vh] overflow-y-auto rounded-2xl bg-white text-left shadow-xl;
}

.dialog-head {
  @apply sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-gray-100 bg-white px-5 py-3.5;
}

.dialog-title {
  @apply text-lg font-bold tracking-tight text-gray-900;
}

.dialog-close {
  @apply -mr-2 flex h-10 w-10 items-center justify-center rounded-lg text-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700;
}

.dialog-body {
  @apply space-y-5 px-5 py-5 text-[0.9375rem] break-keep;
}

.dialog-label {
  @apply mb-1.5 text-sm font-semibold text-gray-500;
}

.member-list {
  @apply max-h-[40vh] divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-200;
}

.member-list li {
  @apply flex items-baseline justify-between gap-3 px-4 py-2.5;
}

.choice {
  @apply block cursor-pointer;
}

.choice-input {
  @apply sr-only;
}

.choice-box {
  @apply flex h-11 items-center justify-center rounded-xl border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400;
}

.choice-input:checked + .choice-box {
  @apply border-main-navy bg-main-navy/5 font-semibold text-main-navy ring-1 ring-main-navy;
}

.choice-input:focus-visible + .choice-box {
  @apply ring-4 ring-main-navy/20;
}

.refund-box {
  @apply space-y-1.5 rounded-xl bg-slate-50 px-4 py-3.5;
}

.dialog-submit {
  @apply flex h-12 w-full items-center justify-center rounded-xl bg-main-navy text-[0.9375rem] font-bold text-white transition-colors hover:bg-main-navy/90;
}

/* 페이지 이동 - 모바일은 이전/다음 버튼을 크게, 태블릿 이상은 번호 */
.pager {
  /* 전역 nav 패딩(1rem 1.5rem) 제거 */
  @apply mt-8 grid grid-cols-[1fr_auto_1fr] items-center gap-3 p-0 sm:flex sm:justify-center sm:gap-2;
}

.pager-step {
  @apply inline-flex h-12 w-full sm:h-10 sm:w-auto items-center justify-center gap-1 rounded-xl border border-gray-300 bg-white px-4 text-[0.9375rem] sm:text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-white;
}

.pager-status {
  @apply min-w-[4.5rem] text-center text-[0.9375rem] text-gray-500;
}

.pager-num {
  @apply flex h-10 min-w-[2.5rem] items-center justify-center rounded-xl px-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100;
}

.pager-num--active {
  @apply bg-main-navy text-white hover:bg-main-navy;
}

.pager-gap {
  @apply flex h-10 w-6 items-center justify-center text-gray-400;
}
</style>
